/**
 * タグ分析とステータス更新サービス
 * testresult_checkアプリ内で完結する実装
 */

// import { TestTarget } from './api';  // タグ生成をai_sstf_controllerに一元化したため不要

interface TagStats {
  ng_rate: number;
  total: number;
  ng_count: number;
}

interface TestCase {
  id: number;
  name?: string;  // primary_item, secondary_item, tertiary_itemは廃止
}

interface TestResult {
  id: number;
  status: string;
  value: any;
  test_case_id: number;
  test_case?: TestCase;
}

export class TagAnalysisService {
  private ngThreshold: number = 0.7;  // 70%（後方互換性のため保持）
  private minSamples: number = 1;     // 最小サンプル数（後方互換性のため保持）
  private maxSamples: number = 100;   // 信頼性100%になるサンプル数
  private uncertaintyWeight: number = 0.3;  // 不確実性ペナルティの重み
  private scoreThreshold: number = 70;  // 要確認判定の閾値（スコアがこれ未満で要確認）
  // private testTargets: TestTarget[] = [];  // テストターゲットのキャッシュ（不要になったためコメントアウト）

  /**
   * タグを解析用に分割
   */
  private parseTags(tagString: string | undefined): string[] {
    if (!tagString) return [];
    return tagString.split(',').map(t => t.trim()).filter(t => t);
  }

  /**
   * タグNG率を計算（action_tagsのみを使用）
   */
  calculateTagNgRates(
    testResults: TestResult[],
    actionTags: string[]
  ): Record<string, TagStats> {
    const tagStats: Record<string, TagStats> = {};

    // 人間判定済みの結果のみを対象とする
    const confirmedResults = testResults.filter(result => {
      const value = typeof result.value === 'string' ? JSON.parse(result.value) : result.value;
      return value?.tag_analysis?.human_judgment_confirmed === true;
    });

    // action_tagsについて統計を計算
    actionTags.forEach(tag => {
      if (!tag) return;

      const tagKey = `action_tags:${tag}`;
      let total = 0;
      let ngCount = 0;

      confirmedResults.forEach(result => {
        const value = typeof result.value === 'string' ? JSON.parse(result.value) : result.value;
        const tagAnalysis = value?.tag_analysis || {};
        const evaluatedTags = tagAnalysis.evaluated_tags || {};
        const actionTagList = evaluatedTags.action_tags || [];

        if (actionTagList.includes(tag)) {
          total++;
          if (!tagAnalysis.judgment_match) {
            ngCount++;
          }
        }
      });

      tagStats[tagKey] = {
        ng_rate: total > 0 ? ngCount / total : 0,
        total,
        ng_count: ngCount
      };
    });

    return tagStats;
  }

  /**
   * スコアペナルティを計算（不確実性ペナルティ方式）
   * @param ngRate NG率 (0.0-1.0)
   * @param totalCount 判定回数
   * @returns ペナルティ値 (0-100)
   */
  calculateScorePenalty(ngRate: number, totalCount: number): number {
    // 信頼性係数（0.0 ～ 1.0）
    const confidence = this.maxSamples > 0
      ? Math.min(1.0, totalCount / this.maxSamples)
      : 0.0;

    // 不確実性ペナルティ（信頼度が低いほど高い）
    const uncertaintyPenalty = (1 - confidence) * this.uncertaintyWeight;

    // 総ペナルティ = NG率 + 不確実性ペナルティ
    const penalty = (ngRate + uncertaintyPenalty) * 100;

    return Math.min(100.0, penalty);
  }

  /**
   * 要確認判定（不確実性ペナルティ方式・action_tagsのみを使用）
   * ペナルティを計算し、仮想スコア(100-ペナルティ)が閾値未満なら要確認
   */
  checkReviewRequired(
    actionTags: string[],
    tagStats: Record<string, TagStats>
  ): { required: boolean; reason: string | null } {
    // タグが設定されていない場合
    if (!actionTags || actionTags.length === 0) {
      return { required: false, reason: null };
    }

    // 各タグのペナルティを計算し、最大ペナルティを持つタグを特定
    let maxPenalty = 0;
    let maxPenaltyTag = '';
    let maxPenaltyNgRate = 0;
    let maxPenaltyTotal = 0;
    let maxPenaltyConfidence = 0;
    let maxPenaltyUncertainty = 0;

    for (const tag of actionTags) {
      if (!tag) continue;

      const tagKey = `action_tags:${tag}`;
      const stats = tagStats[tagKey] || { ng_rate: 0, total: 0, ng_count: 0 };

      const penalty = this.calculateScorePenalty(stats.ng_rate, stats.total);

      if (penalty > maxPenalty) {
        maxPenalty = penalty;
        maxPenaltyTag = tag;
        maxPenaltyNgRate = stats.ng_rate;
        maxPenaltyTotal = stats.total;
        maxPenaltyConfidence = this.maxSamples > 0
          ? Math.min(1.0, stats.total / this.maxSamples)
          : 0.0;
        maxPenaltyUncertainty = (1 - maxPenaltyConfidence) * this.uncertaintyWeight;
      }
    }

    // 仮想スコア = 100 - ペナルティ
    const virtualScore = 100 - maxPenalty;

    // 仮想スコアが閾値未満なら要確認
    if (virtualScore < this.scoreThreshold && maxPenaltyTag) {
      const reason = `アクション「${maxPenaltyTag}」` +
        `(判定${maxPenaltyTotal}回, ` +
        `NG率${(maxPenaltyNgRate * 100).toFixed(0)}%, ` +
        `信頼度${(maxPenaltyConfidence * 100).toFixed(0)}%, ` +
        `不確実性ペナルティ${(maxPenaltyUncertainty * 100).toFixed(0)}%) ` +
        `→ スコア${virtualScore.toFixed(0)}点 < 閾値${this.scoreThreshold}点`;
      return {
        required: true,
        reason
      };
    }

    return { required: false, reason: null };
  }

  /**
   * ステータスに要確認を付与/解除
   */
  updateStatusWithReview(currentStatus: string, reviewRequired: boolean): string {
    const baseStatus = currentStatus.replace('(要確認)', '');
    return reviewRequired ? `${baseStatus}(要確認)` : baseStatus;
  }

  // 以下のメソッドはai_sstf_controllerでタグ生成を行うため不要になりました
  // 今後の参考のためコメントアウトして保持
  
  // /**
  //  * テストターゲットデータを設定
  //  * @param testTargets テストターゲットのリスト
  //  */
  // setTestTargets(testTargets: TestTarget[]): void {
  //   this.testTargets = testTargets;
  // }

  // /**
  //  * test_target_idから画面(view)情報とタイプを取得
  //  * @param testTargetId テストターゲットID
  //  * @returns 画面名とタイプ情報
  //  */
  // private getTestTargetInfo(testTargetId: string): { view?: string; viewId?: string; type?: string } {
  //   const target = this.testTargets.find(t => t.testTargetId === testTargetId);
  //   if (!target) {
  //     return {};
  //   }

  //   // 親テストターゲットを再帰的に走査してtype.name="View"を探す
  //   let view: string | undefined;
  //   let viewId: string | undefined;
  //   let currentTargetId = target.parentTestTargetId;
    
  //   while (currentTargetId) {
  //     const parent = this.testTargets.find(t => t.testTargetId === currentTargetId);
  //     if (!parent) {
  //       break;
  //     }
      
  //     // type.name が "View" の場合、それが画面
  //     if (parent.type && parent.type.name === 'View') {
  //       view = parent.name;
  //       viewId = parent.testTargetId;
  //       break;
  //     }
      
  //     // さらに親を探す
  //     currentTargetId = parent.parentTestTargetId;
  //   }

  //   // descriptionから「タイプ：xxx」を抽出
  //   let type: string | undefined;
  //   if (target.description) {
  //     const typeMatch = target.description.match(/タイプ[：:]\s*(\S+)/);
  //     if (typeMatch) {
  //       type = typeMatch[1];
  //     }
  //   }

  //   return { view, viewId, type };
  // }

  // /**
  //  * アクションタグを生成（test_target_idから画面とタイプ情報を追加）
  //  * @param action アクション名
  //  * @param testTargetId テストターゲットID
  //  * @param testTargetName テストターゲット名
  //  * @param existingTags 既存のアクションタグ
  //  * @returns 拡張されたアクションタグの配列
  //  */
  // generateActionTags(
  //   action: string,
  //   testTargetId: string,
  //   testTargetName?: string,
  //   existingTags: string[] = []
  // ): string[] {
  //   const tags = [...existingTags];
    
  //   // action:id:nameの形式で追加
  //   const target = this.testTargets.find(t => t.testTargetId === testTargetId);
  //   const targetName = testTargetName || target?.name || '';
  //   if (action && testTargetId) {
  //     tags.push(`${action}:${testTargetId}:${targetName}`);
  //   }
    
  //   const { view, viewId, type } = this.getTestTargetInfo(testTargetId);

  //   // test_target_id:画面名の形式で追加
  //   if (view && viewId) {
  //     tags.push(`${viewId}:${view}`);
  //   }

  //   if (type) {
  //     tags.push(`type:${type}`);
  //   }

  //   return tags;
  // }

  /**
   * ステップレベルのタグNG率を計算（action_tagsのみを使用）
   * @param testResults 判定済みのテスト結果
   * @param actionTags 評価対象のアクションタグ
   * @returns タグごとの統計情報
   */
  calculateStepLevelTagNgRates(
    testResults: TestResult[],
    actionTags: string[]
  ): Record<string, TagStats> {
    const tagStats: Record<string, TagStats> = {};

    console.log('calculateStepLevelTagNgRates入力:', {
      testResultsCount: testResults.length,
      actionTagsCount: actionTags.length
    });

    // 人間判定済みの結果のみを対象とする
    const confirmedResults = testResults.filter(result => {
      let value = result.value;
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value);
        } catch (e) {
          return false;
        }
      }
      const hasConfirmed = value?.tag_analysis?.human_judgment_confirmed === true;
      if (!hasConfirmed && result === testResults[0]) {
        console.log('最初の結果の判定状態:', {
          hasTagAnalysis: !!value?.tag_analysis,
          hasHumanJudgmentConfirmed: value?.tag_analysis?.human_judgment_confirmed,
          hasSteps: !!value?.tag_analysis?.steps,
          stepsCount: value?.tag_analysis?.steps?.length
        });
      }
      return hasConfirmed;
    });

    console.log(`フィルタ後の人間判定済み結果: ${confirmedResults.length}/${testResults.length}件`);

    // アクションタグについて統計を計算
    actionTags.forEach(tag => {
      if (!tag) return;

      const tagKey = `action_tags:${tag}`;
      let total = 0;
      let ngCount = 0;

      // 各テスト結果のステップを走査
      confirmedResults.forEach(result => {
        let value = result.value;
        // valueがJSON文字列の場合はパース
        if (typeof value === 'string') {
          try {
            value = JSON.parse(value);
          } catch (e) {
            return;
          }
        }
        // tag_analysisまたは直接stepsを確認
        const tagAnalysis = value?.tag_analysis || value || {};
        const steps = tagAnalysis.steps || [];

        // 各ステップを確認
        steps.forEach((step: any) => {
          // 未評価ステップは除外
          if (step.human_judgment === '未評価') {
            return;
          }

          const stepTags = step.evaluated_tags || {};
          const stepActionTags = stepTags.action_tags || [];

          if (stepActionTags.includes(tag)) {
            total++;
            // judgment_matchがfalseまたはai_interpretation_correctがfalseの場合はNG
            if (step.judgment_match === false || step.ai_interpretation_correct === false) {
              ngCount++;
            }
          }
        });
      });

      tagStats[tagKey] = {
        ng_rate: total > 0 ? ngCount / total : 0,
        total,
        ng_count: ngCount
      };
    });

    return tagStats;
  }

  /**
   * ステップレベルの要確認判定（不確実性ペナルティ方式・action_tagsのみを使用）
   * @param actionTags 評価対象のアクションタグ
   * @param tagStats ステップレベルのタグ統計
   * @returns 要確認判定結果
   */
  checkStepLevelReviewRequired(
    actionTags: string[],
    tagStats: Record<string, TagStats>
  ): { required: boolean; reason: string | null } {
    // checkReviewRequiredと同じロジックを使用
    return this.checkReviewRequired(actionTags, tagStats);
  }

  /**
   * 最大ペナルティを持つタグの情報を取得
   * @param actionTags 評価対象のアクションタグ
   * @param tagStats タグ統計
   * @returns 最大ペナルティのタグ情報
   */
  getMaxPenaltyTagInfo(
    actionTags: string[],
    tagStats: Record<string, TagStats>
  ): {
    tag: string | null;
    penalty: number;
    ngRate: number;
    total: number;
    confidence: number;
    uncertaintyPenalty: number;
  } | null {
    if (!actionTags || actionTags.length === 0) {
      return null;
    }

    let maxPenalty = 0;
    let result: {
      tag: string | null;
      penalty: number;
      ngRate: number;
      total: number;
      confidence: number;
      uncertaintyPenalty: number;
    } | null = null;

    actionTags.forEach(tag => {
      if (!tag) return;

      const tagKey = `action_tags:${tag}`;
      const stats = tagStats[tagKey] || { ng_rate: 0, total: 0, ng_count: 0 };

      const penalty = this.calculateScorePenalty(stats.ng_rate, stats.total);

      if (penalty > maxPenalty) {
        maxPenalty = penalty;
        const confidence = this.maxSamples > 0
          ? Math.min(1.0, stats.total / this.maxSamples)
          : 0.0;
        result = {
          tag,
          penalty,
          ngRate: stats.ng_rate,
          total: stats.total,
          confidence,
          uncertaintyPenalty: (1 - confidence) * this.uncertaintyWeight
        };
      }
    });

    return result;
  }
}